/**
 * Collection Search Modal - Full-screen overlay search
 * Integrates with existing collection-search.js logic
 * Shows results in full-screen modal at top of screen
 */

/* START: Modal-specific CSS and JS for full-screen collection search */

(function(window, document) {
  'use strict';

  class CollectionSearchModal {
    constructor(modal, trigger) {
      if (!modal || !trigger) return;

      this.modal = modal;
      this.trigger = trigger;
      this.collectionHandle = this.getCollectionHandle();

      // Config
      this.config = {
        debounceDelay: 300,
        productsPerPage: 250,
        maxDisplayResults: 50,
        minSearchChars: 2,
        descriptionLength: 100
      };

      // DOM elements
      this.overlay = modal.querySelector('[data-modal-overlay]');
      this.container = modal.querySelector('.collection-search-modal-container');
      this.input = modal.querySelector('[data-modal-search-input]');
      this.resultsSection = modal.querySelector('[data-modal-results-section]');
      this.resultsGrid = modal.querySelector('[data-modal-results-grid]');
      this.resultsCount = modal.querySelector('[data-modal-results-count]');
      this.clearBtn = modal.querySelector('[data-modal-clear]');
      this.closeBtn = modal.querySelector('[data-modal-close]');

      // State
      this.allProducts = [];
      this.filteredProducts = [];
      this.isFetching = false;
      this.isFetched = false;
      this.debounceTimer = null;
      this.searchTerm = '';
      this.displayedCount = 0;
      this.isLoadingMore = false;
      this.loadMoreObserver = null;
      this.isOpen = false;

      if (this.input && this.resultsGrid && this.collectionHandle) {
        this.init();
      } else {
        console.warn('CollectionSearchModal: Missing required elements or collection handle');
      }
    }

    init() {
      this.bindEvents();
      this.setupInfiniteScroll();
    }

    setupInfiniteScroll() {
      // Create IntersectionObserver for infinite scroll
      this.loadMoreObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isLoadingMore) {
            this.loadMoreProducts();
          }
        });
      }, {
        root: this.resultsGrid,
        rootMargin: '100px',
        threshold: 0.1
      });
    }

    bindEvents() {
      // Trigger button - open modal
      this.trigger.addEventListener('click', () => {
        this.openModal();
      });

      // Overlay click - close modal
      if (this.overlay) {
        this.overlay.addEventListener('click', () => {
          this.closeModal();
        });
      }

      // Escape key - close modal
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.closeModal();
        }
      });

      // Input - search as you type
      this.input.addEventListener('input', (e) => {
        this.handleInput(e.target.value);
      });

      // Clear button - clears search text and hides results
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          this.clearSearch();
          this.input.focus();
        });
      }

      // Prevent form submission
      const form = this.modal.querySelector('form');
      if (form) {
        form.addEventListener('submit', (e) => e.preventDefault());
      }

      // Handle Add to Cart clicks
      this.resultsGrid.addEventListener('click', (e) => {
        const cartBtn = e.target.closest('.search-product-btn-cart');
        if (cartBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.addToCart(cartBtn);
        }
      });

      // Focus trap - prevent tab outside modal
      this.modal.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && this.isOpen) {
          this.trapFocus(e);
        }
      });
    }

    openModal() {
      this.isOpen = true;
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Focus input
      setTimeout(() => {
        this.input.focus();
      }, 100);

      // Start fetching products in background
      if (!this.isFetched && !this.isFetching) {
        this.fetchAllProducts();
      }
    }

    closeModal() {
      this.isOpen = false;
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.clearSearch();
    }

    trapFocus(e) {
      const focusableElements = this.modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }

    getCollectionHandle() {
      const handle = this.modal.dataset.collectionHandle;
      if (handle) return handle;

      const match = window.location.pathname.match(/\/collections\/([^\/\?]+)/);
      return match ? match[1] : null;
    }

    handleInput(value) {
      const term = value.trim().toLowerCase();
      this.searchTerm = term;

      clearTimeout(this.debounceTimer);

      if (!term) {
        this.hideResultsSection();
        this.filteredProducts = [];
        return;
      }

      if (term.length < this.config.minSearchChars) {
        this.hideResultsSection();
        return;
      }

      this.debounceTimer = setTimeout(() => {
        this.performSearch(term);
      }, this.config.debounceDelay);
    }

    async performSearch(term) {
      if (!this.isFetched) {
        if (!this.isFetching) {
          await this.fetchAllProducts();
        } else {
          await this.waitForFetch();
        }
      }

      this.filteredProducts = this.filterProducts(term);
      this.renderResults(term);
    }

    waitForFetch() {
      return new Promise((resolve) => {
        const checkFetch = setInterval(() => {
          if (this.isFetched) {
            clearInterval(checkFetch);
            resolve();
          }
        }, 100);
      });
    }

    async fetchAllProducts() {
      if (this.isFetching || this.isFetched) return;

      this.isFetching = true;

      try {
        let page = 1;
        let hasMore = true;

        while (hasMore) {
          const url = `/collections/${this.collectionHandle}/products.json?limit=${this.config.productsPerPage}&page=${page}`;
          const response = await fetch(url);

          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const data = await response.json();

          if (data.products && data.products.length > 0) {
            this.allProducts = this.allProducts.concat(data.products);
            page++;

            // Update results while fetching
            if (this.searchTerm) {
              this.filteredProducts = this.filterProducts(this.searchTerm);
              this.renderResults(this.searchTerm, true);
            }

            if (data.products.length < this.config.productsPerPage) {
              hasMore = false;
            }
          } else {
            hasMore = false;
          }
        }

        this.isFetched = true;
        console.log(`CollectionSearchModal: Loaded ${this.allProducts.length} products`);

        if (this.searchTerm) {
          this.filteredProducts = this.filterProducts(this.searchTerm);
          this.renderResults(this.searchTerm);
        }

      } catch (error) {
        console.error('CollectionSearchModal: Fetch error', error);
        this.showMessage('Error loading products. Please try again.');
      } finally {
        this.isFetching = false;
      }
    }

    filterProducts(term) {
      const searchWords = term.toLowerCase().split(/\s+/).filter(w => w.length > 0);

      return this.allProducts.filter(product => {
        const searchableText = [
          product.title || '',
          product.vendor || '',
          product.product_type || '',
          (product.tags || []).join(' '),
          product.handle || '',
          product.body_html ? this.stripHtml(product.body_html) : ''
        ].join(' ').toLowerCase();

        return searchWords.every(word => searchableText.includes(word));
      });
    }

    stripHtml(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    }

    renderResults(term, isPartial = false) {
      if (!this.resultsGrid) return;

      const totalFound = this.filteredProducts.length;

      // Check if this is truly a NEW search (different term) vs just updating counts
      const currentCards = this.resultsGrid.querySelectorAll('.collection-search-product-card');
      const shouldClearGrid = currentCards.length === 0 || !isPartial;

      if (shouldClearGrid) {
        // Reset displayed count for new search
        this.displayedCount = 0;
        this.resultsGrid.innerHTML = '';

        // Disconnect existing observer
        if (this.loadMoreObserver) {
          this.loadMoreObserver.disconnect();
        }
      }

      // Show results section
      this.showResultsSection();

      // Update count
      if (this.resultsCount) {
        if (isPartial && this.isFetching) {
          this.resultsCount.innerHTML = `<span class="loading-text">(${totalFound} found, loading...)</span>`;
        } else if (totalFound === 0) {
          this.resultsCount.textContent = '';
        } else {
          this.resultsCount.textContent = `(${totalFound})`;
        }
      }

      if (totalFound === 0 && !this.isFetching) {
        this.resultsGrid.innerHTML = `
          <div class="collection-search-no-results">
            <p>No products match "${this.escapeHtml(term)}"</p>
            <p class="hint">If you don't see what you're looking for, please click below</p>
            <a href="/collections/all" class="btn btn-border">Browse All Products</a>
          </div>
        `;
        return;
      }

      // Only append products if we cleared the grid (new search)
      if (shouldClearGrid) {
        this.appendProducts(term, this.config.maxDisplayResults);
      }
    }

    appendProducts(term, count) {
      const startIndex = this.displayedCount;
      const endIndex = Math.min(startIndex + count, this.filteredProducts.length);
      const productsToAdd = this.filteredProducts.slice(startIndex, endIndex);

      // Remove existing load more element if present
      const existingLoadMore = this.resultsGrid.querySelector('.collection-search-load-more');
      if (existingLoadMore) {
        existingLoadMore.remove();
      }

      productsToAdd.forEach(product => {
        const card = this.createProductCard(product, term);
        this.resultsGrid.appendChild(card);
      });

      this.displayedCount = endIndex;

      // Update count display
      if (this.resultsCount) {
        const totalFound = this.filteredProducts.length;
        if (this.isFetching) {
          this.resultsCount.innerHTML = `<span class="loading-text">(${totalFound} found, loading...)</span>`;
        } else {
          this.resultsCount.textContent = `(${totalFound})`;
        }
      }

      // Add load more trigger if there are more products
      if (this.displayedCount < this.filteredProducts.length) {
        this.addLoadMoreTrigger();
      }
    }

    addLoadMoreTrigger() {
      const loadMore = document.createElement('div');
      loadMore.className = 'collection-search-load-more';
      loadMore.innerHTML = `
        <svg class="collection-search-load-more-spinner" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" opacity="0.25"/>
          <path d="M10 2a8 8 0 0 1 8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <span>Loading more products...</span>
      `;
      this.resultsGrid.appendChild(loadMore);

      // Observe the load more element
      if (this.loadMoreObserver) {
        this.loadMoreObserver.observe(loadMore);
      }
    }

    loadMoreProducts() {
      if (this.isLoadingMore || this.displayedCount >= this.filteredProducts.length) return;

      this.isLoadingMore = true;

      // Small delay for smooth UX
      setTimeout(() => {
        this.appendProducts(this.searchTerm, this.config.maxDisplayResults);
        this.isLoadingMore = false;
      }, 300);
    }

    createProductCard(product, searchTerm) {
      const card = document.createElement('div');
      card.className = 'collection-search-product-card';

      // Image
      const imageUrl = product.images && product.images[0]
        ? product.images[0].src.replace(/\.([^\.]+)$/, '_300x.$1')
        : 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';

      // Price
      const variant = product.variants && product.variants[0];
      const price = variant ? this.formatMoney(variant.price) : '';
      const comparePrice = variant && variant.compare_at_price
        ? this.formatMoney(variant.compare_at_price)
        : '';

      // Description - truncate to config length
      const description = product.body_html
        ? this.truncateText(this.stripHtml(product.body_html), this.config.descriptionLength)
        : '';

      // Highlight matching text in title
      const highlightedTitle = this.highlightMatches(product.title, searchTerm);

      // Variant ID for add to cart
      const variantId = variant ? variant.id : '';

      card.innerHTML = `
        <a href="/products/${product.handle}" class="search-product-image">
          <img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy" onload="this.classList.add('loaded')">
        </a>
        <div class="search-product-content">
          <div class="search-product-price">
            ${comparePrice ? `<span class="compare-price">${comparePrice}</span>` : ''}
            <span>${price}</span>
          </div>
          <div class="search-product-info">
            <h4 class="search-product-title">
              <a href="/products/${product.handle}">${highlightedTitle}</a>
            </h4>
            ${description ? `<p class="search-product-description">${this.escapeHtml(description)}</p>` : ''}
          </div>
          <div class="search-product-footer">
            <div class="search-product-buttons">
              <a href="/products/${product.handle}" class="search-product-btn search-product-btn-view">View Product</a>
              <button type="button"
                      class="search-product-btn search-product-btn-cart"
                      data-variant-id="${variantId}"
                      ${!variant || !variant.available ? 'disabled' : ''}>
                ${variant && variant.available ? 'Add To Cart' : 'Sold Out'}
              </button>
            </div>
          </div>
        </div>
      `;

      // Handle cached images that load instantly
      const img = card.querySelector('img');
      if (img && img.complete) {
        img.classList.add('loaded');
      }

      return card;
    }

    truncateText(text, maxLength) {
      if (!text) return '';
      text = text.trim();
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength).trim() + '...';
    }

    highlightMatches(text, term) {
      if (!term) return this.escapeHtml(text);

      const words = term.split(/\s+/).filter(w => w.length > 0);
      let result = this.escapeHtml(text);

      words.forEach(word => {
        const regex = new RegExp(`(${this.escapeRegex(word)})`, 'gi');
        result = result.replace(regex, '<mark>$1</mark>');
      });

      return result;
    }

    formatMoney(price) {
      // Shopify API returns price as string in dollars (e.g., "15.00")
      const amount = parseFloat(price).toFixed(2);
      return `$${amount}`;
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    async addToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const originalText = button.textContent;
      button.textContent = 'Adding...';
      button.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });

        if (response.ok) {
          button.textContent = 'Added!';
          button.style.background = '#2a6';
          button.style.borderColor = '#2a6';

          // Update cart count if there's a cart counter element
          this.updateCartCount();

          setTimeout(() => {
            button.textContent = originalText;
            button.style.background = '';
            button.style.borderColor = '';
            button.disabled = false;
          }, 2000);
        } else {
          throw new Error('Add to cart failed');
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        button.textContent = 'Error';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
      }
    }

    updateCartCount() {
      // Try to update common cart count selectors
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          const countElements = document.querySelectorAll('.cart-count, .cart-quantity, [data-cart-count], .tt-cart-box .tt-badge-cart');
          countElements.forEach(el => {
            el.textContent = cart.item_count;
          });
        })
        .catch(() => {});
    }

    showResultsSection() {
      if (this.resultsSection) {
        this.resultsSection.style.display = 'flex';
      }
    }

    hideResultsSection() {
      if (this.resultsSection) {
        this.resultsSection.style.display = 'none';
      }
      if (this.resultsGrid) {
        this.resultsGrid.innerHTML = '';
      }
      if (this.resultsCount) {
        this.resultsCount.textContent = '';
      }
    }

    // Loader removed - no loading indicator needed

    clearSearch() {
      this.input.value = '';
      this.searchTerm = '';
      this.filteredProducts = [];
      this.displayedCount = 0;

      // Disconnect observer
      if (this.loadMoreObserver) {
        this.loadMoreObserver.disconnect();
      }

      this.hideResultsSection();
    }
  }

  // Auto-initialize
  function init() {
    const modal = document.querySelector('[data-collection-search-modal]');
    const trigger = document.querySelector('[data-collection-search-trigger]');

    if (modal && trigger && !modal.dataset.initialized) {
      const instance = new CollectionSearchModal(modal, trigger);
      modal.dataset.initialized = 'true';

      // Bind search suggestion buttons
      bindSearchSuggestions(instance);
    }
  }

  // Bind search suggestion buttons to open modal and search
  function bindSearchSuggestions(modalInstance) {
    const suggestionButtons = document.querySelectorAll('.search-suggestion');

    suggestionButtons.forEach(button => {
      button.addEventListener('click', function() {
        const searchText = this.textContent.trim();

        // Open the modal
        modalInstance.openModal();

        // Set the search text in the input
        if (modalInstance.input) {
          modalInstance.input.value = searchText;

          // Trigger the search
          modalInstance.handleInput(searchText);
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.CollectionSearchModal = CollectionSearchModal;

})(window, document);

/* END: Modal-specific CSS and JS for full-screen collection search */
